import { formatCompact } from "../../utils/math";
import type { ProfileProgress } from "../progression/levels";
import type { OrbitForgeSave } from "../save/types";
import { calculateUpgradeEffects } from "./effects";
import { getUpgradeDefinition, upgradePanelOrder, type UpgradeKind, type UpgradeLevels } from "./definitions";
import { calculateRollbackRefund, calculateUpgradePrice } from "./pricing";

export interface UpgradeRowView {
  readonly kind: UpgradeKind;
  readonly label: string;
  readonly level: number;
  readonly maxLevel: number;
  readonly unlockLevel: number;
  readonly locked: boolean;
  readonly maxed: boolean;
  readonly affordable: boolean;
  readonly price: number;
  readonly rollbackRefund: number;
  readonly canRollback: boolean;
  readonly nextEffect: string;
}

export interface ForgePanelView {
  readonly rows: readonly UpgradeRowView[];
  readonly totalUpgradeLevels: number;
  readonly visibleRows: number;
}

export function buildForgePanelView(save: OrbitForgeSave, progress: ProfileProgress): ForgePanelView {
  const rows = upgradePanelOrder
    .map((kind) => buildUpgradeRow(save, progress, kind))
    .filter((row) => !row.locked || row.unlockLevel <= progress.effectiveLevel + 5);

  return {
    rows,
    totalUpgradeLevels: upgradePanelOrder.reduce((sum, kind) => sum + save.upgrades[kind], 0),
    visibleRows: rows.length
  };
}

function buildUpgradeRow(save: OrbitForgeSave, progress: ProfileProgress, kind: UpgradeKind): UpgradeRowView {
  const definition = getUpgradeDefinition(kind);
  const level = save.upgrades[kind];
  const locked = progress.effectiveLevel < definition.unlockLevel;
  const maxed = level >= definition.maxLevel;
  const price = maxed ? 0 : calculateUpgradePrice(definition, level);
  const rollbackRefund = calculateRollbackRefund(definition, level);
  const nextEffect = maxed ? "MAX" : effectText(kind, nextLevels(save.upgrades, kind), progress);

  return {
    kind,
    label: definition.label,
    level,
    maxLevel: definition.maxLevel,
    unlockLevel: definition.unlockLevel,
    locked,
    maxed,
    affordable: !locked && !maxed && save.stats.Credits >= price,
    price,
    rollbackRefund,
    canRollback: progress.effectiveLevel >= 5 && level > 0,
    nextEffect
  };
}

function nextLevels(levels: UpgradeLevels, kind: UpgradeKind): UpgradeLevels {
  return {
    ...levels,
    [kind]: levels[kind] + 1
  };
}

function effectText(kind: UpgradeKind, levels: UpgradeLevels, progress: ProfileProgress): string {
  const effects = calculateUpgradeEffects(levels, progress);
  if (kind === "MaxCores") return `${effects.maxCores} active`;
  if (kind === "CoreValue") return `${formatCompact(effects.coreBaseValue)} base`;
  if (kind === "ChargeBonus") return `+${effects.chargeValue.toFixed(1)} / bounce`;
  if (kind === "ShellSpeed") return `x${effects.shellSpeedMultiplier.toFixed(2)} speed`;
  if (kind === "GateWidth") return `+${effects.gateBonusDegrees.toFixed(1)} gate`;
  if (kind === "GateCount") return `${effects.gateCount} gates`;
  if (kind === "ExtraShell") return `${1 + effects.extraShellCount} shells`;
  if (kind === "CollectorAmp") return `x${effects.collectorAmpMultiplier.toFixed(2)} lanes`;
  if (kind === "OverchargedCore") return `${(effects.overchargedChance * 100).toFixed(2)}% rare`;
  if (kind === "PolaritySync") return `${effects.polarityStackCap} x${(1 + effects.polarityChargePerStack).toFixed(3)}`;
  if (kind === "CriticalDischarge") return `${(effects.criticalChance * 100).toFixed(1)}% crit`;
  if (kind === "OfflineReactor") return `${Math.round(effects.offlineRewardRate * 100)}% return`;
  return effects.shellLensLaneBonusMultiplier > 1 ? `${effects.polarityWindowCount} x${effects.shellLensLaneBonusMultiplier.toFixed(2)}` : `${effects.polarityWindowCount} lens`;
}
